import React, { createContext, useContext, useMemo } from 'react';
import { LOAD_HEADER_DATA } from 'apolloClient/queries/header';
import { useQuery } from '@apollo/client';
import { HeaderData } from '../Navigation';
import { useIsClient } from 'hooks/useIsClient';
import { HeaderDataRaw, toHeaderData } from './toHeaderData';

const defaultVariables = {
  loadBuildings: false,
  loadNhds: false,
};

export const HeaderContext = createContext<{
  fetchMoreBuildings: (query: { page?: number; search?: string }) => void;
  fetchMoreNhds: (search: string) => void;
  data?: HeaderData;
}>({
  fetchMoreBuildings: () => {},
  fetchMoreNhds: () => {},
});

const HeaderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isClient = useIsClient();
  const { fetchMore, data: raw } = useQuery<
    HeaderDataRaw,
    {
      neighborhoodSearch?: string;
      buildingSearch?: string;
      buildingPage?: number;
      loadNhds: boolean;
      loadBuildings: boolean;
      sort?: string;
    }
  >(LOAD_HEADER_DATA, {
    skip: !isClient,
    fetchPolicy: 'no-cache',
    variables: {
      buildingPage: 0,
      buildingSearch: '',
      neighborhoodSearch: '',
      loadBuildings: true,
      loadNhds: true,
      sort: 'name:asc',
    },
  });

  // the menus render the collection shape they were written against
  const data = useMemo(() => toHeaderData(raw), [raw]);

  const fetchMoreBuildings = ({
    page,
    search,
  }: {
    page?: number;
    search?: string;
  }) => {
    fetchMore({
      variables: {
        ...defaultVariables,
        loadBuildings: true,
        buildingPage: page || 1,
        buildingSearch: search,
        sort: 'name:asc',
      },
      updateQuery(previousQueryResult, options) {
        // with no-cache, Apollo passes the last reported result, which is
        // undefined if fetchMore runs before the initial query has resolved
        const previous = previousQueryResult?.headerData;
        const next = options.fetchMoreResult?.headerData?.buildings;

        if (!next) return previousQueryResult;

        return {
          headerData: {
            neighborhoods: previous?.neighborhoods ?? null,
            buildings: {
              ...next,
              items:
                page && page > 1
                  ? (previous?.buildings?.items || []).concat(next.items)
                  : next.items,
            },
          },
        };
      },
    });
  };

  const fetchMoreNhds = (search: string) => {
    fetchMore({
      variables: {
        ...defaultVariables,
        loadNhds: true,
        neighborhoodSearch: search,
      },
      updateQuery(previousQueryResult, options) {
        return {
          headerData: {
            buildings: previousQueryResult?.headerData?.buildings ?? null,
            neighborhoods:
              options.fetchMoreResult?.headerData?.neighborhoods ?? null,
          },
        };
      },
    });
  };
  return (
    <HeaderContext.Provider value={{ fetchMoreBuildings, fetchMoreNhds, data }}>
      {children}
    </HeaderContext.Provider>
  );
};

export const useHeader = () => useContext(HeaderContext);

export default HeaderProvider;
